import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Checkbox,
  Button,
  FormControlLabel,
  Typography,
  Box,
  styled,
  CircularProgress,
} from "@mui/material";
import color from "../../components/utils/Colors";
import {
  getNotificationSettings,
  createNotificationSettings,
  updateNotificationSettings,
} from "../../services/services";
import { useSnackbar } from "../../components/utils/SnackBar";

const validationSchema = Yup.object().shape({
  isTrackCourseVisibility: Yup.boolean(),
  isTrackCourseCompleteness: Yup.boolean(),
  isTrackProfileCompleteness: Yup.boolean(),
  isTrackNewCourse: Yup.boolean(),
  isTrackNewLession: Yup.boolean(),
  isTrackTopicVisibility: Yup.boolean(),
  isTrackNewTopic: Yup.boolean(),
  isTrackSkillEffect: Yup.boolean(),
});

const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
  color: color.firstColor,
  "&.Mui-checked": { color: color.firstColor },
  "&:hover": { backgroundColor: "rgba(76, 175, 80, 0.1)" },
  "&.MuiCheckbox-root": { borderRadius: "4px" },
}));

const defaultValues = {
  isTrackCourseVisibility: false,
  isTrackCourseCompleteness: false,
  isTrackProfileCompleteness: false,
  isTrackNewCourse: false,
  isTrackNewLession: false,
  isTrackTopicVisibility: false,
  isTrackNewTopic: false,
  isTrackSkillEffect: false,
};

const NotificationPreferences = () => {
  const showSnackbar = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsId, setSettingsId] = useState<number | null>(null);
  const [initialValues, setInitialValues] = useState(defaultValues);

  useEffect(() => {
    getNotificationSettings()
      .then((res: any) => {
        const data: any[] = res?.data?.data ?? [];
        if (data.length > 0) {
          const s = data[0];
          setSettingsId(s.id);
          setInitialValues({
            isTrackCourseVisibility: !!s.isTrackCourseVisibility,
            isTrackCourseCompleteness: !!s.isTrackCourseCompleteness,
            isTrackProfileCompleteness: !!s.isTrackProfileCompleteness,
            isTrackNewCourse: !!s.isTrackNewCourse,
            isTrackNewLession: !!s.isTrackNewLession,
            isTrackTopicVisibility: !!s.isTrackTopicVisibility,
            isTrackNewTopic: !!s.isTrackNewTopic,
            isTrackSkillEffect: !!s.isTrackSkillEffect,
          });
        }
      })
      .catch(() => {
        console.error("Failed to load notification settings");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (values: typeof defaultValues) => {
    setSaving(true);
    try {
      if (settingsId) {
        await updateNotificationSettings(settingsId, values);
      } else {
        const res: any = await createNotificationSettings(values);
        if (res?.data?.data?.id) setSettingsId(res.data.data.id);
      }
      showSnackbar("Notification preferences saved successfully!");
    } catch {
      showSnackbar("Failed to save preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress sx={{ color: color.firstColor }} />
      </Box>
    );
  }

  const preferenceItems = [
    {
      name: "isTrackCourseVisibility",
      label: "Track Course Visibility",
      description: "Get notified when course visibility changes.",
    },
    {
      name: "isTrackCourseCompleteness",
      label: "Track Course Completeness",
      description: "Get notified about your course completion progress.",
    },
    {
      name: "isTrackProfileCompleteness",
      label: "Track Profile Completeness",
      description: "Get notified when your profile needs updating.",
    },
    {
      name: "isTrackNewCourse",
      label: "New Course Announcements",
      description: "Get notified when new courses are added.",
    },
    {
      name: "isTrackNewLession",
      label: "New Lesson Notifications",
      description:
        "Get notified when new lessons are added to your enrolled courses.",
    },
    {
      name: "isTrackTopicVisibility",
      label: "Track Topic Visibility",
      description: "Get notified when topic visibility changes.",
    },
    {
      name: "isTrackNewTopic",
      label: "New Topic Notifications",
      description: "Get notified when new topics are added.",
    },
    {
      name: "isTrackSkillEffect",
      label: "Skill Effect Notifications",
      description: "Get notified about changes affecting your skills.",
    },
  ];

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSave}
    >
      {({ values, handleChange }) => (
        <Form>
          <Box
            sx={(theme) => ({
              marginTop: "25px",
              flex: 1,
              p: "3%",
              borderRadius: "16px",
              bgcolor:
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(2px)",
            })}
          >
            <Typography
              variant="h5"
              align="center"
              sx={{
                background: color.textColor,
                textAlign: "center",
                color: "white",
                width: "fit-content",
                margin: "auto",
                padding: "4px 16px",
                borderRadius: "4px",
                mb: 4,
              }}
            >
              Notification Preferences
            </Typography>

            {preferenceItems.map((item, idx) => (
              <React.Fragment key={item.name}>
                <FormControlLabel
                  control={
                    <Field
                      as={CustomCheckbox}
                      name={item.name}
                      checked={(values as any)[item.name]}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <Box>
                      <Typography>
                        <strong>{item.label}</strong>
                      </Typography>
                      <Typography variant="caption">
                        {item.description}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "row-reverse",
                    width: "100%",
                    mt: 2,
                  }}
                />
                {idx < preferenceItems.length - 1 && <hr />}
              </React.Fragment>
            ))}

            {/* Save Button */}
            <Box textAlign="center">
              <Button
                type="submit"
                id="save-notification-preferences"
                disabled={saving}
                style={{
                  background: saving ? "#ccc" : color.firstColor,
                  color: "white",
                  borderRadius: "8px",
                  marginTop: 40,
                  minWidth: 120,
                }}
              >
                {saving ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default NotificationPreferences;
